import { JSX } from "react";
import { EntityTypeEnum } from "@app/projects/enumerators";
import { ProjectRunConfigurationResult } from "@app/projects/projectRunConfigurationResult";
import { ServicingProvider } from "@app/projects/results/servicing-provider";
import ContractEntityProfile from "@app/projects/servicing-provider/provider-affiliations/ContractEntityProfile";
import { GainsPercentEnum, ImpactProviderTableData, NcpEnum, NPF } from "@app/projects/servicing-provider/types";
import { ConfidenceTierBadge } from "@app/shared/ConfidenceTierBadge";
import { FlyoutLink } from "@app/shared/Flyouts";
import { OmniusCargoColumnType } from "@app/shared/griddle/CargoColumns";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { ColumnConfig } from "@app/shared/waffles";
import { TableType, ViewableField } from "@app/viewable-fields/viewableFields";
import { restrictByFields } from "@app/viewable-fields/utilities";
import { formatUnconvertedDateCustom } from "@app/shared/formatDateTime";
import formatPhoneNumber from "@app/shared/formatPhoneNumber";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import ProjectionProvider from "@app/projects/results/projection-provider/projectionProvider";

const translatePercentGains = (percentGains: GainsPercentEnum): string => {
  switch (percentGains) {
    case GainsPercentEnum.All:
      return "All % Gains";
    case GainsPercentEnum.Zero:
      return "0% Gains";
    case GainsPercentEnum.Nonzero:
      return "> 0% Gains";
  }
};

const translateNcpFilter = (ncpValue: NcpEnum): string => {
  switch (ncpValue) {
    case NcpEnum.All:
      return "All Closer Non-Contracted Providers";
    case NcpEnum.NextClosest:
      return "Next Closest Non-Contracted Providers";
    case NcpEnum.NotNextClosest:
      return "Other Closer Non-Contracted Providers";
  }
};

const getEntityIconName = (entityType: string): string => {
  let iconName: string;
  switch (entityType) {
    case EntityTypeEnum.Facility.toLowerCase():
      iconName = QesIcons.BuildingV7;
      break;
    case EntityTypeEnum.Individual.toLowerCase():
    default:
      iconName = QesIcons.UserV1;
      break;
  }

  return iconName;
}

const getEntityIconDescription = (entityType: string): string => {
  let iconDescription: string;
  switch (entityType) {
    case EntityTypeEnum.Facility.toLowerCase():
      iconDescription = "Facility";
      break;
    case EntityTypeEnum.Individual.toLowerCase():
    default:
      iconDescription = "Individual";
      break;
  }

  return iconDescription;
}

const getEntityIconClasses = (entityType: string, additionalClasses?: string): string => {
  let iconClasses: string;
  switch (entityType) {
    case EntityTypeEnum.Facility.toLowerCase():
      iconClasses = "fa fa-building";
      break;
    case EntityTypeEnum.Individual.toLowerCase():
    default:
      iconClasses = "fa fa-user-md";
      break;
  }

  if (additionalClasses) {
    iconClasses.concat(additionalClasses);
  }

  return iconClasses;
};

const getEntityIcon = (entityType: string): QesIcons => {
  switch (entityType) {
    case EntityTypeEnum.Facility.toLowerCase():
      return QesIcons.BuildingV7;
    case EntityTypeEnum.Individual.toLowerCase():
    default:
      return QesIcons.UserV1;
  }
};

// only display 'Individual' or 'Facility' indicators
const getEntityTypeDescription = (entityType: string): string => {
  switch (entityType) {
    case EntityTypeEnum.Facility.toLowerCase():
    case EntityTypeEnum.Individual.toLowerCase():
      return entityType;
    default:
      return null;
  }
};

const toCamelCase = (displayName: string): string => {
  const uppercaseMatch = displayName.match(/^([A-Z]*)[A-Z](?=[a-z])/);
  if (uppercaseMatch) {
    const startingAcronym = uppercaseMatch[1];
    if (startingAcronym) {
      // ex. "VAClaimsVolumeDisplay"
      return startingAcronym.toLowerCase() + displayName.slice(startingAcronym.length)
    } else {
      // The normal case (ex. "FirstName")
      return displayName.at(0).toLowerCase() + displayName.slice(1);
    }
  } else {
    // The entire string is already uppercase or lowercase (ex: SSA, address)
    return displayName.toLowerCase();
  }
}

const buildAdequacyProvidersColumnConfig = (
  viewableFields: ViewableField[],
  tableType: TableType,
  showNppes: boolean,
  showMipsScore: boolean,
  showPciData: boolean,
  showVaClaimsVolumeColumn: boolean,
  showTricareClaimsVolumeColumn: boolean,
  showAvgDist?: boolean,
  showAvgTime?: boolean,
  showProjectionReason?: boolean,
  isNetNewProvidersTable?: boolean,
  isNextClosestImpactConfig?: boolean,
  showExclusionSource?: boolean,
  showLocationConfidenceScores?: boolean,
  showMotiveAppropriatenessScore?: boolean,
  showCareJourneyScores?: boolean,
  showProviderAffiliations?: boolean,
  affiliationsDisplayOnNetworkAccordion?: boolean,
  showProviderGrouping?: boolean,
  showProviderValidations?: boolean,
  isZipProject?: boolean,
  showSpecialtyPredictionModel?: boolean,
  showPgrConfidenceTier?: boolean,
): ColumnConfig => {
  let config: ColumnConfig = {};
  if (!viewableFields || viewableFields.length === 0) {
    return config;
  }

  const fieldsWithoutColumns = [
    NPF.EntityType,
    NPF.NPI,
    NPF.TIN,
    NPF.OrganizationName,

    NPF.Address2,
    NPF.City,
    NPF.State,
    NPF.Zip,
    NPF.Phone,
    NPF.Fax,

    NPF.DeactivatedNPI,
    NPF.MedicareOptOut,
    NPF.OIGExcluded,
    NPF.OIGWaiver,
    NPF.Precluded,
  ];

  const fieldsThatShouldShowAvailability = [
    NPF.DeactivatedNPI,
    NPF.MedicareOptOut,
    NPF.OIGExcluded,
    NPF.OIGWaiver,
    NPF.Precluded,
  ];

  // These aren't in viewable fields
  config = {
    exclusionVerifiedSources: {
      displayText: "Exclusion Verified Sources",
      hidden: !showExclusionSource,
      noSort: true,
      testId: "exclusion-verified-sources",
    },
    lastUpdate: {
      displayText: showExclusionSource ? "Excluded Date" : "Targeted Date",
      hidden: !showProjectionReason,
      noSort: true,
      testId: "projection-last-update",
    },
    lastUpdateBy: {
      displayText: showExclusionSource ? "Excluded By" : "Targeted By",
      hidden: !showProjectionReason,
      noSort: true,
      testId: "projection-last-update-by",
    },
    name: {
      displayText: "Name",
      hidden: false,
      testId: "name",
    },
    primaryContractEntity: {
      displayText: "Primary Contract Entity",
      hidden: false,
      noSort: true,
      testId: "primary-contract-entity"
    },
    projectionReason: {
      displayText: "Reason",
      hidden: !showProjectionReason,
      noSort: true,
      testId: "projection-reason",
    },
    projectionReasonDescription: {
      displayText: "Comment",
      hidden: !showProjectionReason,
      noSort: true,
      testId: "projection-description",
    },
    recordingCriteria: {
      displayText: "Recording Criteria",
      hidden: !(showProjectionReason || tableType === TableType.ProviderTerminations) || isNetNewProvidersTable,
      noSort: true,
      testId: "recording-criteria",
    },
    terminationReason: {
      displayText: "Reason",
      hidden: tableType !== TableType.ProviderTerminations,
      noSort: true,
      testId: "termination-reason",
    },
  };

  // viewable fields also includes cargo columns
  for (const field of viewableFields) {
    if (fieldsWithoutColumns.includes(field.backingFieldName as NPF)) {
      if (field.visible && fieldsThatShouldShowAvailability.includes(field.backingFieldName as NPF)) {
        config.availability = {
          displayText: "Availability",
          hidden: false,
          noSort: true,
          testId: "availability",
        };
      }
      continue;
    }
    const isCargo = field.isCargoColumn;
    const camelCase = isCargo ? field.defaultDisplayName : toCamelCase(field.backingFieldName)
    config[camelCase] = {
      cargoTitle: isCargo ? field.defaultDisplayName : undefined,
      displayText: field.displayName || field.defaultDisplayName,
      hidden: field.fieldExclude || !(field.visible || field.alwaysVisible) || fieldsWithoutColumns.includes(field.backingFieldName as NPF),
      testId: field.displayName.replace(/ /g, "-").toLowerCase(),
    };
  }

  // special NCPF config...
  if (tableType === TableType.NextClosestNetworkProviders) {
    config.averageDistance = {
      ...config.averageDistance,
      hidden: !(tableType === TableType.NextClosestNetworkProviders && showAvgDist),
      tooltip: "Average distance from provider to members without access",
    };
    config.averageTime = {
      ...config.averageTime,
      hidden: !(tableType === TableType.NextClosestNetworkProviders && showAvgTime),
      tooltip: "Average time from provider to members without access",
    };
    config.isServicing = {
      ...config.isServicing,
      hidden: tableType !== TableType.NextClosestNetworkProviders,
      tooltip: "Provides access to other members",
    };
    config.memberCount = {
      ...config.memberCount,
      hidden: tableType !== TableType.NextClosestNetworkProviders,
      tooltip: "Closest provider for this many members without access",
    };
  }

  // one-off tooltip...
  if (config.daysSinceLastAttested) {
    config.daysSinceLastAttested.tooltip = "Days since most recent attestation received; including validations, invalidations and enrichments";
  }

  // special visibility rules...
  if(config.professionalContractEntityName) {
    config.professionalContractEntityName.hidden = (tableType === TableType.NetworkProviders ? !affiliationsDisplayOnNetworkAccordion : !showProviderAffiliations);
  }

  if (config.contractEntitySubType) {
    config.contractEntitySubType.hidden = (tableType === TableType.NetworkProviders ? !affiliationsDisplayOnNetworkAccordion : !showProviderAffiliations);
    config.contractEntitySubType.displayElement = (
      <>
        {<TooltipWrapper containerClassName={"entity-type-tooltip-wrapper"} text={buildTooltipText()} position={"left"}>
          <QesIcon
            className={"waffles-tooltip-indicator align-text-top"}
            iconName={QesIcons.InfoCircle}
            description={"Show tooltip"}
          />
          Type
        </TooltipWrapper>}
      </>)
  }

  if(config.institutionalContractEntityName) {
    config.institutionalContractEntityName.hidden = (tableType === TableType.NetworkProviders ? !affiliationsDisplayOnNetworkAccordion : !showProviderAffiliations);
  }

  if (config.nppesCertificationDate && config.nppesLastUpdatedDate) {
    config.nppesCertificationDate.hidden = !showNppes || !viewableFields.find((f) => f.backingFieldName === NPF.NppesCertificationDate && f.visible && !f.fieldExclude);
    config.nppesLastUpdatedDate.hidden = !showNppes || !viewableFields.find((f) => f.backingFieldName === NPF.NppesLastUpdatedDate && f.visible && !f.fieldExclude);
  }

  if (config.mipsScore) {
    config.mipsScore.hidden = !showMipsScore || !viewableFields.find((f) => f.backingFieldName === NPF.MipsScore && f.visible && !f.fieldExclude);
  }

  if (config.commercialClaimsVolumeDisplay) {
    config.commercialClaimsVolumeDisplay.hidden = !showPciData || !viewableFields.find((f) => f.backingFieldName === NPF.CommercialClaimsVolume && f.visible && !f.fieldExclude);
    config.commercialClaimsVolumeDisplay.tooltip = getClaimsVolumeDefinitions();
  }

  if (config.commercialNewPatientClaims) {
    config.commercialNewPatientClaims.hidden = !showPciData || !viewableFields.find((f) => f.backingFieldName === NPF.CommercialNewPatientClaims && f.visible && !f.fieldExclude);
    config.commercialNewPatientClaims.tooltip = getNewPatientClaimsDefinitions();
  }

  if (config.medicaidClaimsVolumeDisplay) {
    config.medicaidClaimsVolumeDisplay.hidden = !showPciData || !viewableFields.find((f) => f.backingFieldName === NPF.MedicaidClaimsVolume && f.visible && !f.fieldExclude);
    config.medicaidClaimsVolumeDisplay.tooltip = getClaimsVolumeDefinitions();
  }

  if (config.medicaidNewPatientClaims) {
    config.medicaidNewPatientClaims.hidden = !showPciData  || !viewableFields.find((f) => f.backingFieldName === NPF.MedicaidNewPatientClaims && f.visible && !f.fieldExclude);
    config.medicaidNewPatientClaims.tooltip = getNewPatientClaimsDefinitions();
  }

  if (config.medicareClaimsVolumeDisplay) {
    config.medicareClaimsVolumeDisplay.hidden = !showPciData || !viewableFields.find((f) => f.backingFieldName === NPF.MedicareClaimsVolume && f.visible && !f.fieldExclude);
    config.medicareClaimsVolumeDisplay.tooltip = getClaimsVolumeDefinitions();
  }

  if (config.medicareNewPatientClaims) {
    config.medicareNewPatientClaims.hidden = !showPciData || !viewableFields.find((f) => f.backingFieldName === NPF.MedicareNewPatientClaims && f.visible && !f.fieldExclude);
    config.medicareNewPatientClaims.tooltip = getNewPatientClaimsDefinitions();
  }

  if (config.vaClaimsVolumeDisplay) {
    config.vaClaimsVolumeDisplay.hidden = !showVaClaimsVolumeColumn || !viewableFields.find((f) => f.backingFieldName === NPF.VAClaimsVolume && f.visible && !f.fieldExclude);
    config.vaClaimsVolumeDisplay.tooltip = getClaimsVolumeDefinitions();
  }

  if (config.vaNewPatientClaims) {
    config.vaNewPatientClaims.hidden = !showVaClaimsVolumeColumn || !showPciData || !viewableFields.find((f) => f.backingFieldName === NPF.VANewPatientClaims && f.visible && !f.fieldExclude);
    config.vaNewPatientClaims.tooltip = getNewPatientClaimsDefinitions();
  }

  if (config.tricareClaimsVolumeDisplay) {
    config.tricareClaimsVolumeDisplay.hidden = !showTricareClaimsVolumeColumn || !viewableFields.find((f) => f.backingFieldName === NPF.TricareClaimsVolume && f.visible && !f.fieldExclude);
    config.tricareClaimsVolumeDisplay.tooltip = getClaimsVolumeDefinitions();
  }

  if (config.tricareNewPatientClaims) {
    config.tricareNewPatientClaims.hidden = !showTricareClaimsVolumeColumn || !showPciData || !viewableFields.find((f) => f.backingFieldName === NPF.TricareNewPatientClaims && f.visible && !f.fieldExclude);
    config.tricareNewPatientClaims.tooltip = getNewPatientClaimsDefinitions();
  }

  if (config.totalClaimsVolumeDisplay) {
    config.totalClaimsVolumeDisplay.hidden = !showPciData || !viewableFields.find((f) => f.backingFieldName === NPF.TotalClaimsVolume && f.visible && !f.fieldExclude);
    config.totalClaimsVolumeDisplay.tooltip = getClaimsVolumeDefinitions();
  }

  if (config.totalNewPatientClaims) {
    config.totalNewPatientClaims.hidden = !showPciData || !viewableFields.find((f) => f.backingFieldName === NPF.TotalNewPatientClaims && f.visible && !f.fieldExclude);
    config.totalNewPatientClaims.tooltip = getNewPatientClaimsDefinitions();
  }

  if (config.locationConfidenceScoresDisplay) {
    config.locationConfidenceScoresDisplay.hidden = !showLocationConfidenceScores || !viewableFields.find((f) => f.backingFieldName === NPF.LocationConfidenceScore && f.visible && !f.fieldExclude);
    config.locationConfidenceScoresDisplay.tooltip = getLocationConfidenceScoresDefinitions();
  }

  if (config.specialtyPredictionModel) {
    config.specialtyPredictionModel.hidden = !showSpecialtyPredictionModel || !viewableFields.find((f) => f.backingFieldName === NPF.SpecialtyPredictionModel && f.visible && !f.fieldExclude);
    config.specialtyPredictionModel.tooltip = getSpecialtyPredictionModelDefinitions();
  }

  if (config.pgrConfidenceTierDisplay) {
    config.pgrConfidenceTierDisplay.hidden = !showPgrConfidenceTier || !viewableFields.find((f) => f.backingFieldName === NPF.PgrConfidenceTier && f.visible && !f.fieldExclude);
    config.pgrConfidenceTierDisplay.tooltip = CONFIDENCE_LEVEL_TOOLTIP;
    // Waffles measures column widths before the badge loads, so size to the rendered badge instead.
    config.pgrConfidenceTierDisplay.sizeContentToFit = true;
  }

  if (config.effectivenessRank) {
    config.effectivenessRank.hidden = !showPciData || !viewableFields.find((f) => f.backingFieldName === NPF.EffectivenessRank && f.visible && !f.fieldExclude);
    config.effectivenessRank.tooltip = getEfficiencyAndEffectivenessDefinitions(NPF.EffectivenessRank);
  }

  if (config.efficiencyRank) {
    config.efficiencyRank.hidden = !showPciData || !viewableFields.find((f) => f.backingFieldName === NPF.EfficiencyRank && f.visible && !f.fieldExclude);
    config.efficiencyRank.tooltip = getEfficiencyAndEffectivenessDefinitions(NPF.EfficiencyRank);
  }

  if (config.motiveScore) {
    config.motiveScore.displayElement = (
      <div style={{ alignItems: "center", display: "flex" }}>
        <TooltipWrapper text={getMotiveScoreDefinitions()} position="bottom">
          <div className="waffles-tooltip-indicator align-text-top"><QesIcon iconName={QesIcons.InfoCircle} description="Info" width="14" /></div>
        </TooltipWrapper>
        {"Motive Appropriateness Tier"}
        <img src={`${window.customUIBaseUrl}/images/motive-h-color.svg`} alt="Motive Appropriateness Tier" height="30" width="90" className="align-top mx-1" />
      </div>)
    config.motiveScore.hidden = !showMotiveAppropriatenessScore || !viewableFields.find((f) => f.backingFieldName === NPF.MotiveScore && f.visible && !f.fieldExclude);
    config.motiveScore.tooltip = null;
  }

  if (config.careJourneyScoresCostScore) {
    config.careJourneyScoresCostScore.displayElement = (
      <div style={{ alignItems: "center", display: "flex", minWidth: "175px" }}>
        <TooltipWrapper text={getCareJourneyDefinitions()} position="bottom">
          <div className="waffles-tooltip-indicator align-text-top"><QesIcon iconName={QesIcons.InfoCircle} description="Info" width="14"/></div>
        </TooltipWrapper>
        {"CareJourney Cost"}
        <img src={`${window.customUIBaseUrl}/images/carejourney-logo.svg`} alt="CareJourney " width="110" height="30" className="align-top mx-1" />
      </div>);
    config.careJourneyScoresCostScore.hidden = !showCareJourneyScores || !viewableFields.find((f) => f.backingFieldName === NPF.CareJourneyScoresCostScore && f.visible && !f.fieldExclude);
    config.careJourneyScoresCostScore.tooltip = null;
  }

  if (config.careJourneyScoresQualityScore) {
    config.careJourneyScoresQualityScore.displayElement = (
      <div style={{ alignItems: "center", display: "flex", minWidth: "200px" }}>
        <TooltipWrapper text={getCareJourneyDefinitions()} position="bottom">
          <div className="waffles-tooltip-indicator align-text-top"><QesIcon iconName={QesIcons.InfoCircle} description="Info" width="14" /></div>
        </TooltipWrapper>
        {"CareJourney Quality"}
        <img src={`${window.customUIBaseUrl}/images/carejourney-logo.svg`} alt="CareJourney " width="120" height="30" className="align-top mx-1" />
      </div>);
    config.careJourneyScoresQualityScore.hidden = !showCareJourneyScores || !viewableFields.find((f) => f.backingFieldName === NPF.CareJourneyScoresQualityScore && f.visible && !f.fieldExclude);
    config.careJourneyScoresQualityScore.tooltip = null;
  }

  if (config.providerGrouping) {
    config.providerGrouping.hidden = !showProviderGrouping || !viewableFields.find((f) => f.backingFieldName === NPF.ProviderGrouping && f.visible && !f.fieldExclude);
  }

  if (config.providerValidations) {
    config.providerValidations.hidden = !showProviderValidations || !viewableFields.find((f) => f.backingFieldName === NPF.ProviderValidations && f.visible && !f.fieldExclude);
  }

  // next closest impact provider rules...
  if (isNextClosestImpactConfig) {
    config.totalAverageDistance.hidden = !showAvgDist;
    config.totalAverageDistance.tooltip = "The average distance for which the provider is closer than the closest network provider for that member, but is not necessarily the member's closest provider";
    config.totalAverageTime.hidden = !showAvgTime;
    config.totalAverageTime.tooltip = "The average time for which the provider is closer than the closest network provider for that member, but is not necessarily the member's closest provider";
    config.totalMemberCount.tooltip = "The count of members for whom the provider is closer than the next closest network provider";
    config.averageDistance.hidden = !showAvgDist;
    config.averageDistance.tooltip = "The average distance for which the provider is the next closest provider for the member";
    config.averageTime.hidden = !showAvgTime;
    config.averageTime.tooltip = "The average time for which the provider is the next closest provider for the member";
    config.memberCount.tooltip = "The count of members for whom the provider is the next closest provider";

    config.specialtyCode.hidden = true; // is not used in next closest network providers
  }

  if (config.availability) {
    config.availability.noSort = true;
  }

  const isProspectOrMarketProviderTable = (tableType === TableType.ProspectProviders || tableType === TableType.MarketProviders)
  if (isProspectOrMarketProviderTable)
  {
    if (config.servesPercent) {
      config.servesPercent.tooltip = isZipProject
        ? "The % of beneficiaries in a zip code/specialty combination that have access to the provider at the location shown"
        : "The % of beneficiaries in a county/specialty combination that have access to the provider at the location shown";
    }

    if (config.gainsPercent) {
      config.gainsPercent.tooltip = "The increase to Access % if a user adds a provider at the location shown to the existing Network Providers file";
    }

    if (config.impactsPercent) {
      config.impactsPercent.tooltip = "The new Total Access % if a provider at the location shown is added to the existing Network Providers file";
    }
  }

  return config;
};

const buildCommonImpactProviderColumns = (provider: ServicingProvider, viewableFields: ViewableField[], runConfiguration: ProjectRunConfigurationResult): Partial<ImpactProviderTableData> => {
  const checkIcon = <QesIcon iconName={QesIcons.Check} isDecorative className="align-text-top" />;

  /* oxlint-disable sort-keys */
  return {
    specialtyCode: provider.specialtyCode,
    specialty: provider.specialty,
    address: restrictByFields(viewableFields, [NPF.Address, NPF.Address2, NPF.City, NPF.State, NPF.Zip, NPF.Phone, NPF.Fax], (has) => (
      <>
        {has(NPF.Address) && provider.address ? <>{provider.address}<br /></> : null}
        {has(NPF.Address2) && provider.address2 ? <>{provider.address2}<br /></> : null}
        {has(NPF.City) && provider.city}
        {has(NPF.City) && (has(NPF.State) || has(NPF.Zip)) ? ", " : null}
        {has(NPF.State) && provider.state} {has(NPF.Zip) && provider.zip}
        {has(NPF.Phone) && provider.phone ? <br /> : null}
        {has(NPF.Phone) && provider.phone ?
          <>
            <TooltipWrapper text="Phone" position="bottom">
              <QesIcon iconName={QesIcons.PhoneV1} description="Phone number"className="align-text-top me-1" />
            </TooltipWrapper>
            &nbsp; {formatPhoneNumber(provider.phone)}
          </> :
          null
        }
        {has(NPF.Fax) && provider.fax ? <br /> : null}
        {has(NPF.Fax) && provider.fax ?
          <>
            <TooltipWrapper text="Fax" position="bottom">
              <QesIcon iconName={QesIcons.Printer} description="Fax number" className="align-text-top me-1" />
            </TooltipWrapper>
            &nbsp; {formatPhoneNumber(provider.fax)}
          </> :
          null
        }
      </>
    )),
    pgrConfidenceTierDisplay: provider.pgrConfidenceTier != null
      ? <ConfidenceTierBadge confidenceTier={provider.pgrConfidenceTier} testId="pgr-confidence-tier-badge" />
      : "---",
    exclusionVerifiedSources: (
      <div style={{ maxWidth: "300px", whiteSpace: "normal" }}>
        {(provider as ProjectionProvider).exclusionVerifiedSources?.join("; ") ?? ""}
      </div>
    ),
    prefix: provider.prefix,
    firstName: provider.firstName,
    middleName: provider.middleName,
    lastName: provider.lastName,
    suffix: provider.suffix,
    credentials: provider.credentials,
    gender: provider.gender,
    servesPercent: provider.servesPercent !== undefined ? `${provider.servesPercent.toFixed(1)}%` : "",
    gainsPercent: provider.gainsPercent !== undefined ? `${provider.gainsPercent.toFixed(1)}%` : "---",
    impactsPercent: provider.impactsPercent !== undefined ? `${provider.impactsPercent.toFixed(1)}%` : "---",
    availability: restrictByFields(viewableFields, [NPF.DeactivatedNPI, NPF.MedicareOptOut, NPF.OIGExcluded, NPF.OIGWaiver, NPF.Precluded, NPF.InvalidNPI, NPF.MissingNPI], (has) => (
      <>
        {has(NPF.DeactivatedNPI) && provider.deactivatedNPI && <><span>{checkIcon} Deactivated</span><br /></>}
        {has(NPF.MedicareOptOut) && provider.medicareOptOut && <><RoleRestricted allowedRoles={[Roles.MEDICARE_OPT_OUTS]} deniedRoles={[Roles.TOKEN_USER]}><span>{checkIcon} Medicare Opt-Out</span></RoleRestricted><br /></>}
        {has(NPF.OIGExcluded) && provider.oigExcluded && <><RoleRestricted allowedRoles={[Roles.OIG]} deniedRoles={[Roles.TOKEN_USER]}><span>{checkIcon} OIG Excluded</span></RoleRestricted><br /></>}
        {has(NPF.OIGWaiver) && provider.oigWaiver && <> <RoleRestricted allowedRoles={[Roles.OIG]} deniedRoles={[Roles.TOKEN_USER]}><span>{checkIcon} OIG Waiver</span></RoleRestricted> <br /></>}
        {has(NPF.Precluded) && provider.precluded && <><RoleRestricted allowedRoles={[Roles.PRECLUDED_PROVIDER_USER]} deniedRoles={[Roles.TOKEN_USER]}><span>{checkIcon} Medicare Precluded</span></RoleRestricted><br /></>}
        {has(NPF.InvalidNPI) && provider.invalidNPI && <><span>{checkIcon} Invalid NPI</span><br /></>}
        {has(NPF.MissingNPI) && provider.missingNPI && <><span>{checkIcon} Missing NPI</span><br /></>}
      </>
    )),
    taxonomyCode: provider.taxonomyCode,
    acceptingNewPatients: provider.acceptingNewPatients === undefined ? "---" : provider.acceptingNewPatients ? "Y" : "N",
    employmentStatus: provider.employmentStatus,
    contractType: provider.contractType,
    underContract: provider.underContract,
    medGroupAffiliation: provider.medGroupAffiliation,
    medicareCCN: provider.medicareCCN,
    medicareCertifiedBeds: provider.medicareCertifiedBeds,
    acceptsMedicareAssignment: provider.acceptsMedicareAssignment ? "Yes" : "",
    externZip: provider.externZip,
    externSpecialtyCode: provider.externSpecialtyCode,
    externRowId: provider.externRowId,
    externProviderId: provider.externProviderId,
    externLocationId: provider.externLocationId,
    daysSinceLastAttested: provider.daysSinceLastAttested && provider.daysSinceLastAttested <= runConfiguration.recentAttestationDays ?
      `${provider.daysSinceLastAttested} days` :
      "Not Attested",
    isRecentlyAttested: provider.isRecentlyAttested ? "Y" : "N",
    lastAttestedDate: provider.lastAttestedDate ? formatUnconvertedDateCustom(provider.lastAttestedDate, "YYYY-MM-DD") : "---",
    letterOfIntent: provider.letterOfIntent !== undefined ? provider.letterOfIntent ? "Y" : "N" : "---",
    nppesCertificationDate: provider.nppesCertificationDate ? formatUnconvertedDateCustom(provider.nppesCertificationDate, "YYYY-MM-DD") : "",
    nppesLastUpdatedDate: provider.nppesLastUpdatedDate ? formatUnconvertedDateCustom(provider.nppesLastUpdatedDate, "YYYY-MM-DD") : "",
    latitude: provider.latitude,
    longitude: provider.longitude,
    geoInfoDisplay: provider.geoInfoDisplay,
    countyFIPS: provider.countyFIPS ? provider.countyFIPS : "---",
    countySSA: provider.countySSA,
    countyName: provider.countyName,
    countyState: provider.countyState,
    mipsScore: provider.mipsScore ? provider.mipsScore.toString() : "---",
    effectivenessRank: provider.effectivenessRank ? provider.effectivenessRank.toString() : "---",
    efficiencyRank: provider.efficiencyRank ? provider.efficiencyRank.toString() : "---",
    commercialClaimsVolumeDisplay: provider.commercialClaimsVolumeDisplay ? provider.commercialClaimsVolumeDisplay : "---",
    commercialNewPatientClaims: provider.commercialClaimsVolumeDisplay ? (provider.commercialNewPatientClaims ? provider.commercialNewPatientClaims : "Insufficient") : "---",
    medicaidClaimsVolumeDisplay: provider.medicaidClaimsVolumeDisplay ? provider.medicaidClaimsVolumeDisplay : "---",
    medicaidNewPatientClaims: provider.medicaidClaimsVolumeDisplay ? (provider.medicaidNewPatientClaims ? provider.medicaidNewPatientClaims : "Insufficient") : "---",
    medicareClaimsVolumeDisplay: provider.medicareClaimsVolumeDisplay ? provider.medicareClaimsVolumeDisplay : "---",
    medicareNewPatientClaims: provider.medicareClaimsVolumeDisplay ? (provider.medicareNewPatientClaims ? provider.medicareNewPatientClaims : "Insufficient") : "---",
    vaClaimsVolumeDisplay: provider.vaClaimsVolumeDisplay ? provider.vaClaimsVolumeDisplay : "---",
    vaNewPatientClaims: provider.vaClaimsVolumeDisplay ? (provider.vaNewPatientClaims ? provider.vaNewPatientClaims : "Insufficient") : "---",
    tricareClaimsVolumeDisplay: provider.tricareClaimsVolumeDisplay ? provider.tricareClaimsVolumeDisplay : "---",
    tricareNewPatientClaims: provider.tricareClaimsVolumeDisplay ? (provider.tricareNewPatientClaims ? provider.tricareNewPatientClaims : "Insufficient") : "---",
    totalClaimsVolumeDisplay: provider.totalClaimsVolumeDisplay ? provider.totalClaimsVolumeDisplay : "---",
    totalNewPatientClaims: provider.totalClaimsVolumeDisplay ? (provider.totalNewPatientClaims ? provider.totalNewPatientClaims : "Insufficient") : "---",
    locationConfidenceScoresDisplay: provider.locationConfidenceScoresDisplay ? provider.locationConfidenceScoresDisplay : "---",
    specialtyPredictionModel: provider.specialtyPredictionModelDisplay ?? "---",
    motiveScore: provider.motiveScore ? provider.motiveScore : "---",
    careJourneyScoresCostScore: provider.careJourneyScoresCostScore ? provider.careJourneyScoresCostScore : "---",
    careJourneyScoresQualityScore: provider.careJourneyScoresQualityScore ? provider.careJourneyScoresQualityScore : "---",
    professionalContractEntityName: provider.professionalContractEntityName && provider.totalClaimsVolumeDisplay !== "Ghost" ? getContractEntityFlyoutLink(runConfiguration.showPciData, provider.professionalAffiliationId, provider.professionalContractEntityName, runConfiguration.providerAffiliationVersionId, provider.entityType) : <>---</>,
    contractEntitySubType: provider.contractEntitySubType ? provider.contractEntitySubType : "---",
    institutionalContractEntityName: provider.institutionalContractEntityName && provider.totalClaimsVolumeDisplay !== "Ghost" ? provider.institutionalContractEntityName : "---",
  };
  /* oxlint-enable sort-keys */
}

const buildTooltipText = () => {
  return (<>
    <div className={"entity-type-tooltip-body text-start p-2"}>
      <div className={"header-row row border-bottom py-1"}>
        <div className={"col-6 label-s"}>
          Type
        </div>
        <div className={"col-6 label-s"}>
          Description
        </div>
      </div>
      <div className={"row my-2"}>
        <div className={"col label-tiny"}>
          Small Independent
        </div>
        <div className={"col"}>
          5 or fewer MD/DO Providers
        </div>
      </div>
      <div className={"row my-2"}>
        <div className={"col label-tiny"}>
          Medium Independent
        </div>
        <div className={"col"}>
          6-50 MD/DO Providers
        </div>
      </div>
      <div className={"row my-2"}>
        <div className={"col label-tiny"}>
          Large Independent
        </div>
        <div className={"col"}>
          Over 50 MD/DO Providers
        </div>
      </div>
      <div className={"row my-2"}>
        <div className={"col label-tiny"}>
          Non-IDN Health System
        </div>
        <div className={"col"}>
          Owns a single hospital (provider count can vary)
        </div>
      </div>
      <div className={"row my-2"}>
        <div className={"col label-tiny"}>
          IDN (Integrated Delivery Network)
        </div>
        <div className={"col"}>
          Owns two or more hospitals (provider count can vary)
        </div>
      </div>
    </div>
  </>);
}

const getContractEntityFlyoutLink = (showPerformanceOverview: boolean, professionalAffiliationId: number, professionalContractEntityName: string, providerAffiliationVersionId: number, providerEntityType: string): JSX.Element => {
  return (
    <FlyoutLink
      content={{
        body:
          <ContractEntityProfile
            showPerformanceOverview={showPerformanceOverview}
            contractEntityId={professionalAffiliationId ?? 0}
            contractEntityName={professionalContractEntityName}
            providerEntityType={providerEntityType}
            versionId={providerAffiliationVersionId} />,
        title: "Contract Entity Profile",
      }
      }
      classNames="qes-button-secondary"
      testId="mange-templates-list__template-history-flyout"
    >
      {professionalContractEntityName}
    </FlyoutLink>
  )
}

// this should probably be made a generic with return type Partial<T>. the tricky part is indexing Partial<T> with a variable, i.e. matchingItem.name
const getCargoDataForProvider = (provider: ServicingProvider, viewableFields: ViewableField[], cargoColumns: Set<string>): Partial<unknown> => {
  const providerCargoData: { [x: string]: unknown } = {};
  if (cargoColumns && cargoColumns.size > 0) {
    viewableFields.forEach((column) => {
      const matchingItem = provider.cargoData.find((d) => d.name === column.defaultDisplayName);
      if (matchingItem) {
        if (matchingItem.type === OmniusCargoColumnType.bool) {
          providerCargoData[matchingItem.name] = !!matchingItem.value ? <QesIcon iconName={QesIcons.Check} description="Yes" className="align-text-top" /> : <></>;
        } else {
          providerCargoData[matchingItem.name] = matchingItem.value as string | number;
        }
      }
    });
  }
  return providerCargoData;
}

function getClaimsVolumeDefinitions(): JSX.Element {
  return (
    <div className="text-start">
      <b>Ghost</b>: 0 patients<br />
      <b>Peripheral</b>: &lt;24th percentile in patient volume (based on population density and specialty)<br />
      <b>Standard</b>: 25th-74th percentile in patient volume (based on population density and specialty)<br />
      <b>Core</b>: 75th+ percentile in patient volume (based on population density and specialty)<br />
      <b>Insufficient Data - New</b>: There is no claims activity for this provider but because the NPI was recently issued they have not been labeled as a ghost<br />
      <b>Insufficient Data - State/LOB</b>: There is no claims activity for this provider but because of limited claims coverage in this State/LOB they have not been labeled as a ghost< br />
      <b>Insufficient Data - Specialty</b>: There is no claims activity for this provider but because of limited claims volume for this specialty they have not been labeled as a ghost<br />
      <b>Insufficient Data – Indirect Care</b>: There is no primary attributed claims activity for this provider, only activity for non direct roles; i.e. referring, supervising, ordering, etc.<br />
      <b>RX Only</b>: Claims in which only Prescriptions are present<br />
    </div>
  );
}

function getEfficiencyAndEffectivenessDefinitions(npf: NPF): string {
  switch (npf) {
    case NPF.EfficiencyRank:
      return "Efficiency is a measure of physician cost vs. peers where 5 is the best score (lower cost) and 1 is the worst score (higher cost).  Efficiency measures are based on 1) cost per visit 2) cost per patient.  Standardized pricing methodology is used, meaning efficiency is a measure of comparative utilization intensity and not rate/price differences";
    case NPF.EffectivenessRank:
      return "Effectiveness is a measure of quality where 5 is the best score (higher quality) and 1 is the worst score (lower quality). Scores are relative to other providers in the same specialty and state and are derived from claims insight into 1) Mortality 2) Complications 3) Days in acute care";
    default:
      return;
  }
}

function getLocationConfidenceScoresDefinitions() {
  return (
    <div className="text-start">
      High: 85% or higher confidence practitioner IS at address <br />
      Medium: 51%-85% confidence practitioner IS at address <br />
      Low: 51%-85% confidence practitioner is NOT at address <br />
      Very Low: 85% or higher confidence practitioner is NOT at address
    </div>
  );
}

function getSpecialtyPredictionModelDefinitions() {
  return (
    <div className="text-start">
      This column displays whether the NUCC taxonomy code meets matching logic or not.
    </div>
  );
}

function getMotiveScoreDefinitions(): JSX.Element {
  return (
    <div className="text-start">
      Motive Practice Wisely appropriateness tier for domain. 5 is the highest tier and is assigned to physicians
      whose practice patterns adhere the most to clinical guidelines and evidence based medicine standards relative to their peers.
    </div>
  );
}

function getNewPatientClaimsDefinitions(): JSX.Element {
  return (
    <div className="new-patient-claims__tooltip-text-div" >
      Indicates whether the provider has seen at least 3 new patients in the specified line of business within the past 6 months. Marked as:
      <ul>
        <li><b>Yes</b> - 3 or more new patients seen</li>
        <li><b>No</b> - Fewer than 3 new patients seen</li>
        <li><b>Insufficient</b> - No claims submitted in the past 6 months</li>
        <li><b>{'"---"'}</b> - No claims data available</li>
      </ul>
    </div>
  );
}

function getCareJourneyDefinitions(): JSX.Element {
  return (
    <div className="text-start">
      1: Performing Greatly Below Benchmark <br />
      2: Performing Below Benchmark <br />
      3: Performing at Benchmark <br />
      4: Performing Above Benchmark <br />
      5: Performing Greatly Above Benchmark
    </div>
  );
}

export const CONFIDENCE_LEVEL_TOOLTIP = "Confidence Level combines several independent signals, such as location, specialty, identity, and activity, into one score, so you know how much to rely on this record overall";

export {
  buildAdequacyProvidersColumnConfig,
  buildCommonImpactProviderColumns,
  getCargoDataForProvider,
  getClaimsVolumeDefinitions,
  getEfficiencyAndEffectivenessDefinitions,
  getEntityIconName,
  getEntityIconDescription,
  getEntityIconClasses,
  getEntityIcon,
  getEntityTypeDescription,
  getNewPatientClaimsDefinitions,
  getCareJourneyDefinitions,
  getMotiveScoreDefinitions,
  translateNcpFilter,
  translatePercentGains,
};
